import {
  apiErrorSchema,
  tenantOperatorListSchema,
  tenantOperatorSchema,
  type CreateTenantRequest,
  type UpdateTenantRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiRequestError, API_BASE_URL, apiFetch } from '@/lib/api';

export const tenantsQueryKey = ['tenants'] as const;

export function useTenants() {
  return useQuery({
    queryKey: tenantsQueryKey,
    refetchOnWindowFocus: true,
    queryFn: () => apiFetch('/tenants', tenantOperatorListSchema),
  });
}

export function useCreateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTenantRequest) =>
      apiFetch('/tenants', tenantOperatorSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantsQueryKey });
    },
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTenantRequest }) =>
      apiFetch(`/tenants/${id}`, tenantOperatorSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantsQueryKey });
    },
  });
}

export function useUploadTenantLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch(`${API_BASE_URL}/tenants/${id}/logo`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        const parsed = apiErrorSchema.safeParse(body);
        throw new ApiRequestError(
          parsed.success
            ? parsed.data
            : {
                statusCode: response.status,
                code: 'UNKNOWN',
                message: 'Der Logo-Upload ist fehlgeschlagen.',
                timestamp: new Date().toISOString(),
                path: `/tenants/${id}/logo`,
              },
        );
      }
      return tenantOperatorSchema.parse(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantsQueryKey });
    },
  });
}

export function useClearTenantLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/tenants/${id}/logo`, tenantOperatorSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantsQueryKey });
    },
  });
}
